import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUpdateServiceOrderProduct } from 'hooks/api/service-order-products';
import { ServiceOrderProduct } from 'contracts/service-order-products';
import { Drawer } from 'components/ui/drawer';
import { Form, FormField } from 'components/ui/form';
import { Button } from 'components/ui/ButtonGs';
import { InputGs } from 'components/ui/Forms';
import { ActivityIndicator } from 'styles/components/activityIndicators';
import { Formatter } from 'utils';

import { formSchema, type FormSchema } from './form-schema';
import { useMemo } from 'react';


type OnSubmitPayload = Parameters<
  ReturnType<typeof useUpdateServiceOrderProduct>['updateServiceOrderProduct']
>[0]

export type UpdateWorkedQuantityDrawerRef = {
  open: (serviceOrderProduct: ServiceOrderProduct) => void
  close: () => void
}

type Props = {
  onSubmit: (data: OnSubmitPayload) => void
  loading?: boolean
}

export const UpdateWorkedQuantityDrawer =
  React.forwardRef<UpdateWorkedQuantityDrawerRef, Props>(({
                                                            loading,
                                                            onSubmit,
                                                          }, ref) => {
    const [product, setProduct] = React.useState<ServiceOrderProduct>();
    const [open, setOpen] = React.useState(false);

    const form = useForm<FormSchema>({
      resolver: zodResolver(formSchema),
    });

    const currentQuantity = useWatch({
      control: form.control,
      name: 'workedQuantity',
      defaultValue: product?.workedQuantity || 0,
    });

    const quantityChanged = useMemo(() => {
      return product?.workedQuantity !== +currentQuantity;
    }, [currentQuantity, product?.workedQuantity]);

    const onOpenChange = React.useCallback((open: boolean) => {
      setOpen(open);
      form.reset();
    }, [form]);

    const hydrateForm = React.useCallback((product: ServiceOrderProduct) => {
      form.setValue('workedQuantity', product.workedQuantity);
    }, []);

    const onFormSubmit = React.useCallback((formData: FormSchema) => {
      if (!product) return;

      if (formData.workedQuantity > product.blockedStock) {
        const errorMessage = `A quantidade não pode ser superior à 
          ${Formatter.decimal(product.blockedStock)} ${product.unit}`;
        return form.setError('workedQuantity', {
          message: errorMessage,
        });
      }

      onSubmit({
        serviceOrderProductId: product?.id!,
        payload: {
          workedQuantity: formData.workedQuantity,
        },
      });
    }, [onSubmit, product]);

    React.useImperativeHandle(ref, () => ({
      open: (product) => {
        onOpenChange(true);
        setProduct(product);
        hydrateForm(product);
      },
      close: () => {
        onOpenChange(false);
        setProduct(undefined);
      },
    }), [onOpenChange, hydrateForm]);

    return (
      <Drawer.Root open={open} onOpenChange={onOpenChange}>
        <Drawer.Content>
          <div className="max-w-[800px] w-full mx-auto">
            <Drawer.Header>
              <Drawer.Title className="text-center flex flex-col font-GilroyBold text-gray-600 text-[16px]">
                <span>Alterar quantidade de produto liberado</span>
                <span className={'font-extrabold text-gray-800 text-[40px]'}>
                {product?.materialDescription}
              </span>
              </Drawer.Title>
            </Drawer.Header>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onFormSubmit)}
                className="flex flex-col gap-4 pb-10"
              >
                <FormField
                  control={form.control}
                  name={'workedQuantity'}
                  render={({ field }) => (
                    <InputGs
                      {...field}
                      label={'Quantidade de produto liberado'}
                      type={'number'}
                      min={0}
                      step={0.001}
                    />
                  )}
                />
                <div className={'grid grid-cols-2 gap-4'}>
                  <Button.Root
                    type={'button'}
                    size={'sm'}
                    variant={'secondary'}
                    onClick={() => onOpenChange(false)}
                  >
                    Cancelar
                  </Button.Root>
                  <Button.Root
                    type={'submit'}
                    size={'sm'}
                    disabled={loading || !quantityChanged}
                  >
                    {loading ? <ActivityIndicator /> : 'Salvar'}
                  </Button.Root>
                </div>
              </form>
            </Form>
          </div>
        </Drawer.Content>
      </Drawer.Root>
    );
  });
UpdateWorkedQuantityDrawer.displayName = 'UpdateWorkedQuantityDrawer';